<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-1">
      <button
        data-test="open-claim"
        type="button"
        class="btn-icon btn-icon-default"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <span class="capitalize">
        {{ title.replace('_', ' ') }}
      </span>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td>
      {{ australianCurrency(totalCosts.currentClaim - totalCosts.currentCertified) }}
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget - totalCosts.cumulative) }}
    </td>
  </tr>

  <template v-if="open">
    <VariationOrderCostRow
      v-for="cost in uncategorizedCosts"
      :key="cost.id"
      :claim="$props.claim"
      :variation-order-cost="cost"
      :variation-order="cost.variation_order"
      :show-percentages="$props.showPercentages"
    />

    <VariationOrderCategoryRow
      v-for="group in categorizedGroups"
      :key="group.key"
      :group="group"
      :variation-orders="group.variationOrders"
      :costs="group.costs"
      :claim="$props.claim"
      :show-percentages="$props.showPercentages"
    />

    <tr
      v-if="costs.length === 0"
      class="bg-gray-50"
    >
      <td
        colspan="100%"
        class="text-center p-4 text-gray-500"
      >
        No {{ title.replace('_', ' ') }} found for this claim
      </td>
    </tr>
  </template>
</template>

<script setup lang="ts">
import { australianCurrency, australianNumber } from '@/helpers';
import Claim from '@/models/Claim';
import { VariationOrderType } from '@/models/VariationOrder';
import VariationOrderCost from '@/models/VariationOrderCost';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { computed, ref } from 'vue';
import VariationOrderCategoryRow from './VariationOrderCategoryRow.vue';
import VariationOrderCostRow from './VariationOrderCostRow.vue';

const props = defineProps<{
  costs: VariationOrderCost[];
  claim: Claim;
  title: string;
  variationOrderType: ObjectValues<typeof VariationOrderType>;
  showPercentages?: boolean;
}>();

const variationOrderStore = useVariationOrdersStore();
const categoriesStore = useVariationOrderCategoriesStore();

const variationOrders = computed(() => {
  return variationOrderStore.models.where('type', props.variationOrderType).whereHas(
    'costs',
    (query) => {
      query.where('claim_id', props.claim.id);
    },
  )
    .with(
      'costs',
      (query) => {
        query.where('claim_id', props.claim.id);
      },
    )
    .get();
});

function sumCosts(orders: typeof variationOrders.value) {
  const totals = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
  };

  orders.forEach((variationOrder) => {
    totals.budget += variationOrder.budget;
    totals.previous += variationOrder.previous(props.claim);

    const variationOrderCost = variationOrder.costs.find((cost) => {
      return cost.claim_id === props.claim.id;
    });

    if(variationOrderCost) {
      totals.currentClaim += variationOrderCost.current_claim;
      totals.currentCertified += variationOrderCost.current_certified;
    }
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
}

const totalCosts = computed(() => sumCosts(variationOrders.value));

const categorizedGroups = computed(() => {
  const projectId = props.claim.project_id;

  const categories = categoriesStore.models
    .where('project_id', projectId)
    .whereNull('package_id')
    .orderBy('sort_order')
    .get();

  const groups: {
    key: string;
    label: string;
    costs: VariationOrderCost[];
    variationOrders: typeof variationOrders.value;
  }[] = [];

  categories.forEach((category) => {
    const orders = variationOrders.value.filter((vo) => vo.variation_order_category_id === category.id);
    const costs = props.costs.filter((cost) => cost.variation_order?.variation_order_category_id === category.id);

    if(!orders.length && !costs.length) {
      return;
    }

    groups.push({
      key: `category-${category.id}`,
      label: category.description,
      costs: costs.length ?
        costs :
        props.costs.filter((cost) => orders.some((vo) => vo.id === cost.variation_order_id)),

      variationOrders: orders,
    });
  });

  return groups;
});

const uncategorizedCosts = computed(() => {
  return props.costs.filter((cost) => !cost.variation_order?.variation_order_category_id);
});

const open = ref(false);
</script>

<style scoped></style>
